import { ColorPicker } from '../../../../../../widgets/colorPicker';
import { OutlineLinkButton } from '../../../../../../widgets/buttons';
import { debounce } from 'lodash';
import React from 'react';
import Loader from 'src/components/widgets/loader';
import { showErrorNotification } from 'src/components/widgets/notificationToaster';
import { hexToRgb, T, uploadComposerPreviewImage } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import styled from 'styled-components';

interface ComposerUploadComponentProps {
    passedImgUrl?: string;
    onImagePick: (imgUrl: string, tempImageFileKey?: string) => void | string | Promise<void | string>;
}

const ComposerUploadContainer = styled.div`
    display: flex;
    gap: 20px;
    padding: 20px;
    background-color: ${theme.app.backgroundColor};
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    position: relative;
`;

const ComposerUploadOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 10;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ControlsSection = styled.div`
    flex: 2;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ImagePreviewSection = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const UploadArea = styled.div<{ $isDragging: boolean }>`
    border: 2px dashed ${(props) => (props.$isDragging ? theme.input.color : 'white')};
    border-radius: 8px;
    padding: 5px;
    text-align: center;
    background-color: ${(props) => (props.$isDragging ? `${theme.app.backgroundColor_dark}` : `${theme.app.backgroundColor}`)};
    border-color: ${theme.input.color};
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        opacity: 0.8;
    }
`;

const ImagePreview = styled.div`
    height: 128px;
    border-radius: 8px;
    overflow: hidden;
    background-color: ${theme.app.backgroundColor};
    position: relative;
`;

const StyledImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
`;

const RemoveButton = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    background-color: ${theme.app.secondary};
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    &:hover {
        background-color: ${theme.app.secondary_alt};
    }
`;

const ColorPickerContainer = styled.div`
    margin-top: 16px;
    position: relative;
`;

const ColorPickerDropdown = styled.div<{ $isOpen: boolean }>`
    position: absolute;
    bottom: 100%;
    left: 0;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 10px;
    z-index: 100;
    display: ${(props) => (props.$isOpen ? 'block' : 'none')};
    margin-top: 8px;
`;

const ColorButton = styled(OutlineLinkButton)`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ColorPreview = styled.div<{ color: string }>`
    width: 24px;
    height: 24px;
    border-radius: 4px;
    background-color: ${(props) => props.color || '#ffffff'};
    border: 1px solid #ddd;
`;

const SectionTitle = styled.h4`
    margin: 0 0 8px 0;
    color: ${theme.app.textColor};
`;

const ComposerUploadComponent: React.FC<ComposerUploadComponentProps> = ({ passedImgUrl, onImagePick }) => {
    const [currentImage, setCurrentImage] = React.useState<string | undefined>(passedImgUrl);
    const [isDragging, setIsDragging] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [isColorPickerOpen, setIsColorPickerOpen] = React.useState(false);
    const [selectedColor, setSelectedColor] = React.useState<string>('#ffffff');
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const colorPickerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        setCurrentImage(passedImgUrl);
    }, [passedImgUrl]);

    const validateFile = (file: File): boolean => {
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
        return validTypes.includes(file.type);
    };

    const showUploadError = () =>
        showErrorNotification({
            message: T._('The preview image could not be uploaded. Try again.', 'Admin')
        });

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!validateFile(file)) {
            alert('Please upload a valid image file (PNG, JPG, JPEG, WEBP)');
            return;
        }

        setLoading(true);
        setIsDragging(false);
        setSelectedColor('#ffffff'); // Reset color picker on new upload
        try {
            const image = await uploadComposerPreviewImage(file);
            const finalImageUrl = await onImagePick(image.url, image.key);
            setCurrentImage(finalImageUrl || image.url);
        } catch (error) {
            console.error('Error uploading image:', error);
            showUploadError();
        } finally {
            setLoading(false);
        }
    };

    const handleColorPick = async (color: string) => {
        setSelectedColor(color);
        setIsDragging(false);
        try {
            const rgb = hexToRgb(color);
            if (!rgb) return;

            const canvas = document.createElement('canvas');
            canvas.width = 512;
            canvas.height = 512;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const imgData = ctx.createImageData(canvas.width, canvas.height);
            for (let i = 0; i < imgData.data.length; i += 4) {
                imgData.data[i] = rgb.r; // Red
                imgData.data[i + 1] = rgb.g; // Green
                imgData.data[i + 2] = rgb.b; // Blue
                imgData.data[i + 3] = 255; // Alpha
            }
            ctx.putImageData(imgData, 0, 0);
            const blob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob((value) => {
                    if (value) resolve(value);
                    else reject(new Error('Unable to create the selected color image.'));
                });
            });
            const file = new File([blob], `color_image_${Date.now()}.png`, { type: 'image/png' });
            const image = await uploadComposerPreviewImage(file);
            const finalImageUrl = await onImagePick(image.url, image.key);
            setCurrentImage(finalImageUrl || image.url);
            setIsColorPickerOpen(false);
        } catch (error) {
            console.error('Error uploading color image:', error);
            showUploadError();
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveImage = async () => {
        await onImagePick('');
        setCurrentImage(undefined);
        setSelectedColor('#ffffff'); // Reset color picker when image is removed
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (!file) return;

        if (!validateFile(file)) {
            alert('Please upload a valid image file (PNG, JPG, JPEG, WEBP)');
            return;
        }
        setLoading(true);
        try {
            const image = await uploadComposerPreviewImage(file);
            const finalImageUrl = await onImagePick(image.url, image.key);
            setCurrentImage(finalImageUrl || image.url);
        } catch (error) {
            console.error('Error uploading image:', error);
            showUploadError();
        }
        setLoading(false);
    };

    const handleClickUpload = () => {
        fileInputRef.current?.click();
    };

    const debouncedHandleColorPick = React.useCallback(
        debounce((color: string) => {
            setLoading(true);
            handleColorPick(color);
        }, 300),
        []
    );

    // Close color picker when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
                setIsColorPickerOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <ComposerUploadContainer>
            {loading && (
                <ComposerUploadOverlay>
                    <Loader />
                </ComposerUploadOverlay>
            )}
            <ImagePreviewSection>
                <ImagePreview>
                    {currentImage ? (
                        <>
                            <StyledImage src={currentImage} alt='Uploaded image' />
                            <RemoveButton onClick={handleRemoveImage}>✕</RemoveButton>
                        </>
                    ) : (
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: theme.app.textColor
                            }}
                        >
                            {T._('No image selected', 'Admin')}
                        </div>
                    )}
                </ImagePreview>
            </ImagePreviewSection>
            <ControlsSection>
                <div>
                    <SectionTitle>{T._('Upload Image', 'Admin')}</SectionTitle>
                    <UploadArea
                        $isDragging={isDragging}
                        onClick={handleClickUpload}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        <div style={{ pointerEvents: 'none' }}>
                            <p>{T._('Click to upload or drag and drop', 'Admin')}</p>
                            <p style={{ fontSize: '12px', color: theme.app.textColor }}>{T._('Supported formats: PNG, JPG, JPEG, WEBP', 'Admin')}</p>
                            <input
                                type='file'
                                accept='image/png,image/jpeg,image/jpg,image/webp'
                                onChange={handleImageUpload}
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                            />
                        </div>
                    </UploadArea>
                </div>

                <ColorPickerContainer ref={colorPickerRef}>
                    <SectionTitle>{T._('or Pick a Color', 'Admin')}</SectionTitle>
                    <ColorButton onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}>
                        <ColorPreview color={selectedColor} />
                    </ColorButton>
                    <ColorPickerDropdown $isOpen={isColorPickerOpen}>
                        <ColorPicker onChange={debouncedHandleColorPick} color={selectedColor} />
                    </ColorPickerDropdown>
                </ColorPickerContainer>
            </ControlsSection>
        </ComposerUploadContainer>
    );
};

export default ComposerUploadComponent;
